import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { CAP, useSession } from "@/auth/session";
import { organisations, type CreatedUser } from "@/api/client";
import { ScopeAction, ScopeField } from "@/modules/products/ScopeBar";

import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { StatGrid } from "@/components/StatGrid";
import { ErrorNote } from "@/components/ErrorNote";
import {
  ROLE_LABEL, orgQk, useOrgUsers, useOrganisations, type OrgUserRow,
} from "@/modules/organisations/data";

/** People — every account on the platform, scoped to what the caller may see.
 *
 *  REAL DATA. This page rendered a hardcoded list from `fixtures.ts` until
 *  now, alongside eighteen invented organisations.
 *
 *  `mfa` IS COMPUTED FROM AN ENROLLED SECRET, not from "has a row": a row with
 *  no secret is a half-finished enrolment, and reporting that as protected is
 *  the kind of wrong that only shows up when somebody's account is taken.
 *
 *  SCOPED IN SQL. A company_admin's query cannot return another company's
 *  people, and a `company_id` filter from a scoped caller is INTERSECTED with
 *  their own scope rather than trusted — asking for CMP-014 returns nothing.
 */
const COLS: DataCol<OrgUserRow>[] = [
  { key: "name", label: "Name", width: 170, render: (u) => u.name },
  { key: "login", label: "Login", width: 230, mono: true, render: (u) => u.login_id },
  {
    key: "org", label: "Organisation", width: 120, mono: true,
    render: (u) => u.organisation_id ?? <span className="cap">platform</span>,
  },
  { key: "role", label: "Role", width: 140, render: (u) => ROLE_LABEL[u.role] ?? u.role },
  {
    key: "mfa", label: "MFA", width: 80,
    render: (u) => u.mfa ? <Chip tone="ok">on</Chip> : <Chip tone="warn">off</Chip>,
  },
  {
    key: "verified", label: "Email", width: 100,
    render: (u) => u.email_verified
      ? <Chip tone="ok">verified</Chip>
      : <Chip tone="warn">unverified</Chip>,
  },
  {
    key: "access", label: "Space access", width: 200,
    render: (u) => u.space_access.length
      ? u.space_access.map((a) => `${a.kind}/${a.space}`).join(", ")
      : <span className="cap">none</span>,
  },
  {
    key: "status", label: "Status", width: 110,
    render: (u) => u.status === "active"
      ? <Chip tone="ok">active</Chip>
      : <Chip tone="mute">{u.status}</Chip>,
  },
];

export function OrgUsersPage() {
  const [org, setOrg] = useState("");
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const { can } = useSession();
  // SUPER ADMIN ONLY — creating a person grants a login.
  const mayAuthor = can(CAP.MANAGE_ORGANISATIONS);
  const orgs = useOrganisations();
  const q = useOrgUsers(org || undefined);

  const all = q.data?.users ?? [];
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((u) => u.name.toLowerCase().includes(needle)
      || u.login_id.toLowerCase().includes(needle));
  }, [all, search]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Organisations"
        title="People and their access"
        desc="Every account, and which (product, space) each one may enter."
      />

      <StatGrid stats={[
        { k: "People", v: all.length },
        { k: "With MFA", v: all.filter((u) => u.mfa).length },
        { k: "Platform operators", v: all.filter((u) => u.organisation_id === null).length },
      ]} />

      <div style={{ display: "flex", gap: 10, margin: "10px 0" }}>
        <SearchInput value={search} placeholder="Search people…"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} />
        {/* Only offered when there is more than one to choose between — a
            chooser with one option tells a customer the platform has others
            (CompanyFilter.tsx carries the full story). */}
        {!orgs.data?.scoped && (orgs.data?.organisations.length ?? 0) > 1 && (
          <Select value={org} onChange={(e) => setOrg(e.target.value)}>
            <option value="">All people</option>
            {/* A PLATFORM OPERATOR HAS NO ORGANISATION, so no company id can
                ever select one — Dattatray: "the dropdown picker does not
                have super admin picker." It needs its own option, and the
                client turns it into a separate flag rather than sending a
                fake company id. */}
            <option value="__platform__">Platform operators (no organisation)</option>
            {orgs.data?.organisations.map((o) => (
              <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>
            ))}
          </Select>
        )}
        {mayAuthor && (
          <button className={adding ? "btn ghost" : "btn teal"}
            style={{ marginLeft: "auto" }} onClick={() => setAdding(!adding)}>
            {adding ? "Cancel" : "Add person"}
          </button>
        )}
      </div>

      {adding && <AddPersonRow onDone={() => setAdding(false)} />}

      <div className="section-label">People</div>
      <DataTable rows={rows} cols={COLS} rowKey={(u) => u.user_id}
        empty={<div className="panel panel-pad cap">
          {q.isLoading ? "Loading…" : "No people match."}
        </div>} />
    </div>
  );
}


/** Add a person. Inline, super admin only.
 *
 *  NO PASSWORD FIELD, and that is not an omission — SEC-05: nobody sets
 *  another person's password. The account lands with none and the server
 *  issues a set-your-password link, which is the same path a forgotten
 *  password takes. The link is shown here so an operator can hand it over
 *  directly when mail is slow or not configured.
 *
 *  A SUPER ADMIN HAS NO ORGANISATION and everyone else must have one. That is
 *  a CHECK constraint (0053), not a form rule — so the form mirrors it rather
 *  than inventing it, and the server refuses either mistake anyway.
 */
function AddPersonRow({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const orgs = useOrganisations();
  const [login, setLogin] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("company_user");
  const [orgId, setOrgId] = useState("");
  const [created, setCreated] = useState<CreatedUser | null>(null);

  const isPlatform = role === "super_admin";

  const add = useMutation({
    mutationFn: () => organisations.createUser({
      login_id: login, name, role,
      organisation_id: isPlatform ? null : orgId,
    }),
    onSuccess: (u) => {
      setCreated(u);
      void qc.invalidateQueries({ queryKey: orgQk.users() });
      void qc.invalidateQueries({ queryKey: orgQk.list });
    },
  });

  if (created) {
    return (
      <div className="panel panel-pad" style={{ marginBottom: 12 }}>
        <strong>{created.name}</strong> created — {created.login_id}
        <div className="cap" style={{ marginTop: 6 }}>
          No password was set or sent (SEC-05). They set their own with this
          link, which expires {created.invite_expires_at.slice(0, 16).replace("T", " ")}:
        </div>
        <code style={{ display: "block", marginTop: 6, wordBreak: "break-all" }}>
          {`${window.location.origin}/reset?token=${created.invite_token}`}
        </code>
        <button className="btn ghost" style={{ marginTop: 10 }}
          onClick={() => { setCreated(null); onDone(); }}>Done</button>
      </div>
    );
  }

  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      <ScopeField label="Email" hint="what they sign in with">
        <SearchInput value={login} autoFocus mono placeholder="person@company.com"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setLogin(e.target.value.trim().toLowerCase())} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="Full name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
      <ScopeField label="Role">
        <Select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="company_user">Company User</option>
          <option value="company_admin">Company Admin</option>
          <option value="super_admin">Super Admin (platform)</option>
        </Select>
      </ScopeField>
      <ScopeField label="Organisation"
        hint={isPlatform ? "a platform operator belongs to none" : undefined}>
        <Select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          disabled={isPlatform} style={{ minWidth: 200 }}>
          <option value="">{isPlatform ? "—" : "Select…"}</option>
          {(orgs.data?.organisations ?? []).map((o) => (
            <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>
          ))}
        </Select>
      </ScopeField>
      <ScopeAction>
        <button className="btn teal"
          disabled={!login.includes("@") || !name.trim()
            || (!isPlatform && !orgId) || add.isPending}
          onClick={() => add.mutate()}>
          {add.isPending ? "Creating…" : "Create person"}
        </button>
      </ScopeAction>
      <ErrorNote error={add.error} />
    </div>
  );
}
